import type { DamageType } from './damage.ts';
import type { UniqueId } from './saga.ts';
import type { ForkId } from './skills.ts';

/**
 * Skills as data (docs/PODERES.md §10, stage 2): each skill is a form, a damage type and a program of steps
 * that the simulation runs in order when it is cast (`packages/sim/src/battle/cast.ts`). A variant or a unique
 * that changes the skill shows up in its program as a condition (`when`) or as a number that depends on it;
 * a variant's plain number changes (more range, a wider radius) are a patch on the skill (`VARIANT_PATCH`).
 *
 * The numbers stay where they were (SKILLS, FORKS, SKILL_FX, the class tables); the programs point at them.
 * A field name ('damage', 'radius'…) is the skill's own number as its rank and variant leave it.
 */

/** What a skill looks like and how it reaches (the render's template; what a seal will be able to change). */
export type SkillForm =
  /** One blow at one nightmare. */
  | 'strike'
  /** A cone in front of the hero. */
  | 'cone'
  /** Everything within a circle around a point or the hero. */
  | 'circle'
  /** A line from the hero. */
  | 'line'
  /** From one to the next. */
  | 'chain'
  /** Something thrown or shot that flies. */
  | 'projectile'
  /** Ground that stays and acts for a while. */
  | 'zone'
  /** A line of hexes across the way. */
  | 'wall'
  /** Something that fights on its own. */
  | 'summon'
  /** On one ally. */
  | 'ally'
  /** On the hero itself. */
  | 'self'
  /** The hero goes somewhere. */
  | 'leap'
  /** On every ally around. */
  | 'party';

/** A skill's own number, as its rank and variant leave it. */
export type SkillField = 'damage' | 'radius' | 'duration' | 'stun' | 'slowBp' | 'amount' | 'every' | 'castRange' | 'cooldown';
export const SKILL_FIELDS: ReadonlySet<string> = new Set<SkillField>(['damage', 'radius', 'duration', 'stun', 'slowBp', 'amount', 'every', 'castRange', 'cooldown']);

/**
 * A place in the cast: the hero (`h`), the point aimed at and kept in reach (`p`), the raw aim (`aim`), the main
 * target (`e`), the ally picked (`a`), the nightmare or hero at hand in a loop (`o`, `ho`), where the hero lands
 * (`land`), the far end of a line (`end`), and a point an effect of its own left (`q`).
 */
export type Place = 'h' | 'p' | 'aim' | 'e' | 'a' | 'o' | 'ho' | 'land' | 'end' | 'q';

/**
 * A number in a program. Plain numbers and the skill's fields; the rest are worked out at the cast:
 * - `fx`: a unique's number, grown with its awakening; `uqBp`: what the uniques worn do to this skill's recharge or length;
 * - `fork` / `uq` / `skill`: one number or another by the variant carried, a unique worn, or which skill it is;
 * - `mul` (bp), `times` (plain), `add`, `sub`, `div` (integer), `min`, `max`, `ifLess` (a < b ? then : else);
 * - `pow`: as a skill's damage (presence, fury, +damage of gear and tree, the clan's roar); `pres`: as anything
 *   that isn't damage (a heal, a shield, a golem's health); `struct`: what goes to a building; `heal`: a heal
 *   with the cleric's potency; `healBp`: times that potency alone; `powBp`: times the hero's power alone;
 * - `v`: a value kept earlier in the same cast (`keep`);
 * - `inked`: one number if the nightmare at hand is inked (tier 2 or more), another if not;
 * - `hurtBp`: how much life the hero is missing (bp); `maxHp`: a share (bp) of a hero's life.
 */
export type Num =
  | number
  | SkillField
  | { fx: UniqueId; key: string }
  | { uqBp: 'cooldownBp' | 'durationBp' }
  | { fork: Partial<Record<ForkId, Num>>; else: Num }
  | { uq: UniqueId; then: Num; else: Num }
  /** One number with a unique piece's form carried (docs/UNICAS.md §3: `a`, `b` or `final`), another without. */
  | { form: UniqueId; key: 'a' | 'b' | 'final'; then: Num; else: Num }
  /** The form bits a unique piece is carried in (content `FORM_BIT`; 0: none). */
  | { formBits: UniqueId }
  | { mul: [Num, Num] }
  | { times: [Num, Num] }
  | { add: [Num, Num] }
  | { sub: [Num, Num] }
  | { div: [Num, Num] }
  | { min: [Num, Num] }
  | { max: [Num, Num] }
  | { ifLess: [Num, Num]; then: Num; else: Num }
  | { pow: Num }
  | { pres: Num }
  | { struct: Num }
  | { heal: Num }
  | { healBp: Num }
  | { powBp: Num }
  | { v: string }
  | { inked: Num; else: Num }
  | { hurtBp: true }
  | { maxHp: Num; of: Place };

/** When a step runs (every one of an array). */
export type When =
  | { fork: ForkId }
  | { notFork: ForkId }
  | { uq: UniqueId }
  | { notUq: UniqueId }
  /** A unique piece carried in a form (docs/UNICAS.md §3), or not; `anyForm`: in any of them. */
  | { form: UniqueId; key: 'a' | 'b' | 'final' }
  | { notForm: UniqueId; key: 'a' | 'b' | 'final' }
  | { anyForm: UniqueId }
  /** The skill's field isn't zero. */
  | { has: SkillField }
  /** Someone is there (a picked ally may be nobody). */
  | { at: Place }
  /** The nightmare at hand (or the main target) still stands. */
  | { alive: 'o' | 'e' }
  /** The main target has less than this share of its life (bp). */
  | { lowHp: number }
  /** A kept value is above zero. */
  | { positive: string }
  /** A list isn't empty. */
  | { some: string }
  | readonly When[];

/** Which nightmares a selection keeps (all of them): targetable, not a bone wall, on the ground, corrupt, not the main target. */
export type Pick = 't' | 'wall' | 'ground' | 'corrupt' | 'notE';

/**
 * A value laid on something the cast leaves (a zone, a summon, a projectile, a field on a hero or nightmare):
 * a number, a flag, null (cleared), whose it is, a little record, or one of those only when something holds.
 * - `hid` / `eid` / `oid`: the hero's id, the main target's, the one at hand's; `pid`: the player's;
 * - `after`: that many ticks from now; `is`: whether a condition holds; `facing`: which way a summon looks
 *   (`out`: away from the keep, `fromH`: away from the hero, `h`: the way the hero looks);
 * - `rec`: a record of numbers (and the player); `cells`: the hexes a `cells` step laid out;
 * - any other word (a zone's kind, an animation): that word.
 */
export type Lay =
  | Num
  | string
  | boolean
  | null
  | { hid: true }
  | { eid: true }
  | { oid: true }
  | { pid: true }
  | { after: Num }
  | { is: When }
  | { facing: 'out' | 'fromH' | 'h' }
  | { cells: true }
  | { rec: Record<string, Num | { pid: true }> }
  | { when: When; v: Lay };

interface Base {
  when?: When;
  /** For an effect on a nightmare: on the main target rather than the one at hand. */
  on?: 'e';
}

export type Step = Base &
  (
    // Where and at whom (a step that finds nothing ends the cast without casting).
    | { k: 'enemy' }
    | { k: 'enemyNear'; reach?: Num }
    | { k: 'ally'; self?: true; notSelf?: true }
    | { k: 'hurt'; r: Num }
    | { k: 'point'; range?: Num; ground?: true; from?: Place }
    | { k: 'land'; at?: Place; dx?: number }
    | { k: 'aim' }
    | { k: 'end'; from: Place; range: Num }
    | { k: 'face'; to: Place }
    | { k: 'need'; of: string }
    // Who is caught, into a named list ('hits' if not said).
    | { k: 'near'; at: Place; r: Num; pick?: readonly Pick[]; into?: string; withE?: true; nearest?: Num }
    | { k: 'one'; of: 'e' | 'a'; into?: string }
    | { k: 'cone'; r: Num; deg: Num; into?: string }
    | { k: 'line'; to: Place; w: Num; into?: string }
    | { k: 'allies'; at: Place; r: Num; notSelf?: true; into?: string }
    | { k: 'allyLine'; to: Place; w: Num; notSelf?: true; into?: string }
    | { k: 'buildings'; at: Place; r: Num; into?: string }
    | { k: 'each'; of: string; do: readonly Step[] }
    // On the nightmare at hand.
    | { k: 'dmg'; amt: Num; type?: DamageType | { fork: Partial<Record<ForkId, DamageType>>; else: DamageType }; armor?: 'ignore' | 'pierce'; noReact?: true; steal?: true; times?: number }
    | { k: 'smite'; amt: Num }
    | { k: 'stun'; t: Num; heavy?: true }
    | { k: 'slow'; bp: Num; t: Num }
    | { k: 'ground'; t: Num }
    | { k: 'dot'; dmg: Num; type: 'physical' | 'fire'; every: Num; t: Num }
    | { k: 'poison'; dmg: Num; every: Num; t: Num }
    | { k: 'sting'; dmg: Num; every: Num; t: Num; jump: When }
    | { k: 'mark'; bp: Num; t: Num }
    | { k: 'blind'; t: Num; bp: Num }
    | { k: 'taunt'; t: Num }
    | { k: 'shove'; d: Num }
    | { k: 'acid'; t: Num }
    | { k: 'skipDead' }
    | { k: 'killed' }
    // A field on a hero or a nightmare (`up`: only if it raises it).
    | { k: 'set'; to: Place; f: string; v: Lay; up?: true }
    // What shows.
    | { k: 'boom'; at: Place; r: Num; kind: string | { fork: Partial<Record<ForkId, string>>; else: string } }
    | { k: 'beam'; from: Place; to: Place; kind: string }
    | { k: 'shot'; from: Place; to: Place; kind: string }
    // What the cast leaves on the field (at each spot laid out, or at a place).
    | { k: 'zone'; at: Place | 'spot'; z: Record<string, Lay> }
    | { k: 'bot'; at: Place | 'spot'; b: Record<string, Lay> }
    | { k: 'launch'; from: Place; to: Place; pr: Record<string, Lay> }
    | { k: 'clearZones'; kind: string; by: 'pid' | 'follow' }
    | { k: 'clearBots'; kind?: string }
    | { k: 'capZones'; kind: string; n: Num }
    | { k: 'spots'; pattern: 'pair' | 'ring3' | 'circle'; gap: Num; n?: Num }
    | { k: 'cells'; at: Place; n: Num }
    // Heroes.
    | { k: 'move'; to: Place }
    | { k: 'heal'; to: Place; amt: Num }
    | { k: 'ward'; to: Place; bp: Num; t: Num }
    | { k: 'inspire'; to: Place }
    | { k: 'taunter'; t: Num; r: Num }
    // The cast itself.
    | { k: 'keep'; name: string; v: Num }
    | { k: 'record'; of?: string; n?: Num }
    | { k: 'highlight' }
    | { k: 'stop' }
    // Effects only one skill has (their code in cast.ts, under the same name), with a number if they need one.
    | { k: 'special'; fn: string; n?: Num }
  );

export interface SkillCast {
  form: SkillForm;
  /** The damage type it deals (or the one it looks like, if it deals none). */
  type: DamageType;
  /** `amount` is a count (turrets, wall hexes, jumps…): ranks add one instead of 30 %. */
  count?: true;
  steps: readonly Step[];
  /**
   * The few that end apart (mend, aegis): the cooldown without uniques, the animation of the first two slots, and
   * the event at the target instead of the aimed point.
   */
  end?: { at: Place; anim: 'skill0' | 'skill1' };
}
